import API from 'files/api.js';
import Request from 'files/Request.js';

/*
 * QuoteRequest
 *
 * The one place a quote leaves the browser. Everything that collects a quote —
 * the drawer, the request modal, and later the coverage quiz — comes through
 * here, so when the platform endpoint exists there is exactly one function to
 * repoint and one payload shape to agree on.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * NOT YET LIVE. `Endpoint.ready` is false because the platform side does not
 * exist yet. While it is false, submit() validates and builds the real payload,
 * logs it, and resolves with a synthetic id after a short delay so the calling
 * UI exercises its true loading and confirmation states.
 *
 * To go live: build the endpoint below, set ready to true, delete the stub
 * branch. Nothing else in the application changes.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The agreed contract:
 *
 *   POST /api/v1/dealerships/
 *   {
 *     type: 'replicated_website_quote_request',
 *     dealership_id: 932,
 *     customer:  { first_name, last_name, email, phone, address },
 *     items:     [{ handle, title, quantity }],
 *     quiz_result: { ... } | null,
 *     source:    { url, referrer, web_alias }
 *   }
 *   -> 200 { lead_id: 48213 }
 *
 * No prices are sent, because none exist on this site. The dealership prices
 * the system after seeing the home — that is the entire point of the flow.
 */

const Endpoint = {
    path: '/dealerships/',
    ready: false,
    type: 'replicated_website_quote_request'
};

// The web alias is the first path segment when a dealership is served from a
// shared domain, and absent when they are on their own. Useful to whoever
// picks the lead up, and cheap to send.
const getWebAlias = () => {
    let segment = String(window.location.pathname || '').split('/').filter(Boolean)[0];
    return segment || null;
};

export const buildPayload = ({ customer, dealership, items, quiz }) => {
    return {
        customer: {
            address: customer.address?.trim() || null,
            email: customer.email?.trim(),
            first_name: customer.firstName?.trim(),
            last_name: customer.lastName?.trim(),
            phone: customer.phone?.replace(/[^0-9]/g, '')
        },
        dealership_id: dealership?.id || null,
        items: (items || []).map(item => ({
            handle: item.handle,
            quantity: item.quantity,
            title: item.title
        })),
        quiz_result: quiz || null,
        source: {
            referrer: document.referrer || null,
            url: window.location.href,
            web_alias: getWebAlias()
        },
        type: Endpoint.type
    };
};

export const submit = async (utils, props) => {

    let payload = buildPayload(props);

    // Guard rails the UI also enforces, repeated here because this is the
    // boundary — anything that reaches the platform came through this call.
    if(!payload.dealership_id) {
        throw new Error('We could not identify this dealership. Please refresh the page and try again.');
    }
    if(payload.items.length === 0) {
        throw new Error('Please add at least one product to your quote.');
    }

    if(Endpoint.ready === false) {

        // Refuse to fake a success in production. A visitor seeing "request
        // received" while nothing was sent is a lost lead that nobody finds
        // out about; a visible error is caught the same day. The stub is a
        // development affordance and stays one.
        if(API.dev_env !== true) {
            console.error('[QuoteRequest] submitted in production while the endpoint is not live. Set Endpoint.ready once the platform side exists.');
            throw new Error('We are unable to submit your request right now. Please call us and we will take care of it.');
        }

        console.log('[QuoteRequest] endpoint not live yet — payload that would be sent:', payload);
        await new Promise(resolve => setTimeout(resolve, 900));
        return { lead_id: null, stubbed: true };
    }

    let { lead_id } = await Request.post(utils, Endpoint.path, payload);
    return { lead_id: lead_id, stubbed: false };
};

/*
 * The contact form.
 *
 * Separate from the quote request because it is a different thing arriving in
 * a different place: a quote request carries a device list and becomes a lead,
 * a contact message is a question. The replicated website used to embed the
 * platform's own support form in an iframe for this, which meant the page had
 * no control over its type, spacing, validation or error handling.
 *
 *   POST /api/v1/dealerships/
 *   {
 *     type: 'replicated_website_contact_request',
 *     dealership_id: 932,
 *     customer: { first_name, last_name, email, phone },
 *     message: '...',
 *     source: { url, referrer, web_alias }
 *   }
 *   -> 200 { lead_id }
 *
 * Same not-yet-live handling as submit() above: stubbed in development,
 * refuses to fake a success in production.
 */
export const submitContact = async (utils, { customer, dealership, message }) => {

    let payload = {
        customer: {
            email: customer.email?.trim(),
            first_name: customer.firstName?.trim(),
            last_name: customer.lastName?.trim(),
            phone: customer.phone?.replace(/[^0-9]/g, '') || null
        },
        dealership_id: dealership?.id || null,
        message: message?.trim(),
        source: {
            referrer: document.referrer || null,
            url: window.location.href,
            web_alias: getWebAlias()
        },
        type: 'replicated_website_contact_request'
    };

    if(!payload.dealership_id) {
        throw new Error('We could not identify this dealership. Please refresh the page and try again.');
    }
    if(!payload.message) {
        throw new Error('Please tell us how we can help.');
    }

    if(Endpoint.ready === false) {
        if(API.dev_env !== true) {
            console.error('[QuoteRequest] contact form submitted in production while the endpoint is not live.');
            throw new Error('We are unable to send your message right now. Please call us and we will take care of it.');
        }
        console.log('[QuoteRequest] contact endpoint not live yet — payload that would be sent:', payload);
        await new Promise(resolve => setTimeout(resolve, 900));
        return { lead_id: null, stubbed: true };
    }

    let { lead_id } = await Request.post(utils, Endpoint.path, payload);
    return { lead_id: lead_id, stubbed: false };
};

const QuoteRequest = { buildPayload, submit, submitContact };

export default QuoteRequest;
